// IndexedDB wrapper for offline storage
const DB_NAME = 'qa-builder-pro';
const DB_VERSION = 1;

const STORES = {
  FORMS: 'forms',
  PENDING_SYNC: 'pending_sync',
  USER: 'user',
  TRADES: 'trades'
};

class OfflineDB {
  constructor() {
    this.db = null;
    this.isReady = false;
  }

  async init() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      
      request.onsuccess = () => {
        this.db = request.result;
        this.isReady = true;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        // Forms store
        if (!db.objectStoreNames.contains(STORES.FORMS)) {
          const formsStore = db.createObjectStore(STORES.FORMS, { keyPath: 'form_id' });
          formsStore.createIndex('status', 'status', { unique: false });
          formsStore.createIndex('created_by', 'created_by', { unique: false });
          formsStore.createIndex('updated_at', 'updated_at', { unique: false });
        }

        // Pending sync store
        if (!db.objectStoreNames.contains(STORES.PENDING_SYNC)) {
          const syncStore = db.createObjectStore(STORES.PENDING_SYNC, { keyPath: 'id', autoIncrement: true });
          syncStore.createIndex('type', 'type', { unique: false });
          syncStore.createIndex('timestamp', 'timestamp', { unique: false });
        }

        // User store
        if (!db.objectStoreNames.contains(STORES.USER)) {
          db.createObjectStore(STORES.USER, { keyPath: 'key' });
        }

        // Trades store
        if (!db.objectStoreNames.contains(STORES.TRADES)) {
          db.createObjectStore(STORES.TRADES, { keyPath: 'trade_id' });
        }
      };
    });
  }

  // Generic CRUD operations
  async add(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.add(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async put(storeName, data) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.put(data);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async get(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async getAll(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readonly');
      const store = tx.objectStore(storeName);
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  async delete(storeName, key) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.delete(key);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async clear(storeName) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(storeName, 'readwrite');
      const store = tx.objectStore(storeName);
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // Form-specific operations
  async saveForms(forms) {
    await this.init();
    const tx = this.db.transaction(STORES.FORMS, 'readwrite');
    const store = tx.objectStore(STORES.FORMS);
    
    for (const form of forms) {
      store.put(form);
    }
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getFormsByUser(userId) {
    await this.init();
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction(STORES.FORMS, 'readonly');
      const store = tx.objectStore(STORES.FORMS);
      const index = store.index('created_by');
      const request = index.getAll(userId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // Pending sync operations
  async addPendingSync(type, data) {
    return this.add(STORES.PENDING_SYNC, {
      type,
      data,
      timestamp: new Date().toISOString()
    });
  }

  async getPendingSyncs() {
    return this.getAll(STORES.PENDING_SYNC);
  }

  async clearPendingSync(id) {
    return this.delete(STORES.PENDING_SYNC, id);
  }

  async clearAllPendingSyncs() {
    return this.clear(STORES.PENDING_SYNC);
  }

  // User operations
  async saveUser(user, token) {
    await this.put(STORES.USER, { key: 'current', user, token });
  }

  async getUser() {
    return this.get(STORES.USER, 'current');
  }

  async clearUser() {
    return this.delete(STORES.USER, 'current');
  }

  // Trades operations
  async saveTrades(trades) {
    await this.init();
    const tx = this.db.transaction(STORES.TRADES, 'readwrite');
    const store = tx.objectStore(STORES.TRADES);
    
    // Clear existing trades first
    store.clear();
    
    for (const trade of trades) {
      store.put(trade);
    }
    
    return new Promise((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getTrades() {
    return this.getAll(STORES.TRADES);
  }
}

export const offlineDB = new OfflineDB();
export { STORES };
